<template>
    <div class="files-list__item" :style="gridStyle">
        <div class="files-list__item-left">
            <simple-svg :src="getFileIcon(file.fileType)" custom-class-name="icon" />
            <img v-if="file.imageUrls?.preview" :src="file.imageUrls.preview" class="files-list__item-image"
                @click="isViewerOpen = true" />
            <img v-else-if="file.previewUrl" :src="file.previewUrl" class="files-list__item-image"
                @click="isViewerOpen = true" />
            <div v-else class="files-list__item-image-placeholder"></div>
            <image-viewer v-model="isViewerOpen" :src="file.imageUrls?.full || file.previewUrl" />
            <div class="files-list__item-info">
                <span class="files-list__item-name">{{ file.originalName }}</span>
                <span class="files-list__item-meta">
                    {{ formatFileSize(file.fileSize) }} · {{ formatDateTime(file.createdAt) }}
                    <template v-if="file.projectName"> · {{ file.projectName }}</template>
                </span>
            </div>
        </div>

        <div v-if="showUser" class="files-list__item-user">
            <simple-svg :src="UserIcon" width="16" height="16" custom-class-name="icon" />
            <span>{{ file.uploadedByName }}</span>
        </div>

        <div v-if="showAttachments">
            <div class="files-list__item-att" v-if="file.attachments?.length === 1">
                <simple-svg :src="LinkIcon" width="16" height="16" custom-class-name="icon" />
                <div>
                    <span>{{ entityTypeMap[file.attachments[0].entityType] || file.attachments[0].entityType }}</span>
                </div>
            </div>
            <div class="files-list__item-att" v-else-if="file.attachments?.length > 1">
                <simple-svg :src="LinkIcon" width="16" height="16" custom-class-name="icon" />
                <div>
                    <span>{{ file.attachments.length }} вложений</span>
                </div>
            </div>
        </div>

        <div class="files-list__item-actions">
            <a v-if="file.fileUrl" :href="file.fileUrl" target="_blank">
                <image-button size="20" :src="DownloadIcon" hint="Открыть" />
            </a>
            <a v-else-if="file.imageUrls?.full" :href="file.imageUrls.full" target="_blank">
                <image-button size="20" :src="DownloadIcon" hint="Открыть" />
            </a>
            <image-button v-if="showDelete" size="20" :src="TrashIcon" hint="Удалить"
                @click="$emit('delete', file)" red />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { formatDateTime } from '@/utils/dateUtils';
import { formatFileSize } from '@/utils/fileUtils';
import ImageViewer from '@/components/ui/ImageViewer.vue';
import FileBlankIcon from '@/assets/icons/File_Blank.svg';
import FileDocumentIcon from '@/assets/icons/File_Document.svg';
import FileCheckIcon from '@/assets/icons/File_Check.svg';
import ImageIcon from '@/assets/icons/Image_01.svg';
import DownloadIcon from '@/assets/icons/Download.svg';
import TrashIcon from '@/assets/icons/Trash_Full.svg';
import UserIcon from '@/assets/icons/User_02.svg';
import LinkIcon from '@/assets/icons/Link_Horizontal.svg';

const props = defineProps({
    file: { type: Object, required: true },
    showUser: { type: Boolean, default: false },
    showAttachments: { type: Boolean, default: false },
    entityTypeMap: { type: Object, default: () => ({}) },
    showDelete: { type: Boolean, default: false },
});

defineEmits(['delete']);

const isViewerOpen = ref(false);

const gridStyle = computed(() => {
    const cols = ['1fr'];
    if (props.showUser) cols.push('250px');
    if (props.showAttachments) cols.push('200px');
    cols.push('80px');
    return { gridTemplateColumns: cols.join(' ') };
});

const FILE_ICON_MAP = {
    IMAGE: ImageIcon,
    DOCUMENT: FileDocumentIcon,
    PDF: FileCheckIcon,
    OTHER: FileBlankIcon,
};

const getFileIcon = (fileType) => {
    if (!fileType) return FileBlankIcon;
    return FILE_ICON_MAP[fileType.toString().toUpperCase()] || FileBlankIcon;
};

</script>

<style lang="scss">
.files-list {
    display: flex;
    flex-direction: column;

    &__item-image {
        width: 48px;
        height: 48px;
        border-radius: 4px;
        object-fit: cover;

        &:hover {
            cursor: pointer;
            opacity: 0.8;
        }
    }

    &__item-image-placeholder {
        width: 48px;
        height: 48px;
        border-radius: 4px;
        background-color: var(--color-bg);
    }

    &__item-user {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__item-att {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__item {
        display: grid;
        align-items: center;
        gap: 16px;
        padding: 10px 0;
        border-bottom: 1px solid var(--color-border);

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            background-color: var(--color-bg);

            .files-list__item-actions {
                opacity: 1;
            }
        }
    }

    &__item-left {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    &__item-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    &__item-name {
        font-size: 14px;
        font-weight: 500;
        color: var(--color-text);
        word-break: break-word;
    }

    &__item-meta {
        font-size: 12px;
        color: var(--color-text-dark);
    }

    &__item-actions {
        display: flex;
        align-items: center;
        justify-content: end;
        gap: 4px;
        padding-right: 5px;
        flex-shrink: 0;
        opacity: 0;
    }
}
</style>
